import React, { useState, useMemo } from 'react'
import { ArchiveEntry, Invoice } from '@/types/invoice'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/utils'
import { Archive, Search, Filter, Calendar, User, FileText, Eye, RotateCcw } from 'lucide-react'

interface ArchiveHistoryProps {
  archive: ArchiveEntry[]
  isOpen: boolean
  onClose: () => void
  onRestore: (archiveId: string, invoice: Invoice) => void
}

export function ArchiveHistory({ archive, isOpen, onClose, onRestore }: ArchiveHistoryProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'sent' | 'draft' | 'failed'>('all')
  const [selectedEntry, setSelectedEntry] = useState<ArchiveEntry | null>(null)

  const filteredArchive = useMemo(() => {
    return archive.filter(entry => {
      const matchesSearch = 
        (entry.client?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (entry.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (entry.notes?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
      
      const matchesStatus = statusFilter === 'all' || entry.status === statusFilter
      
      return matchesSearch && matchesStatus
    })
  }, [archive, searchTerm, statusFilter])

  const stats = useMemo(() => {
    const total = archive.length
    const sent = archive.filter(entry => entry.status === 'sent').length
    const draft = archive.filter(entry => entry.status === 'draft').length
    const failed = archive.filter(entry => entry.status === 'failed').length

    return { total, sent, draft, failed }
  }, [archive])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="w-5 h-5" />
            Archive History ({stats.total} entries)
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col h-full">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-sm text-blue-800">Total</div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{stats.sent}</div>
              <div className="text-sm text-green-800">Sent</div>
            </div>
            <div className="bg-yellow-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.draft}</div>
              <div className="text-sm text-yellow-800">Draft</div>
            </div>
            <div className="bg-red-50 p-3 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
              <div className="text-sm text-red-800">Failed</div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by client, invoice number, or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-md"
            >
              <option value="all">All Status</option>
              <option value="sent">Sent</option>
              <option value="draft">Draft</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Archive List */}
          <div className="flex-1 overflow-y-auto">
            {filteredArchive.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Archive className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No archive entries found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredArchive.map((entry) => (
                  <div
                    key={entry.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedEntry(entry)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4">
                          <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                          <h4 className="font-semibold">{entry.client || 'Unknown Client'}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            entry.status === 'sent' ? 'bg-green-100 text-green-800' :
                            entry.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {entry.status}
                          </span>
                        </div>
                        <p className="text-gray-600">Invoice #{entry.invoiceNumber || entry.invoiceId}</p>
                        <p className="text-sm text-gray-500">
                          {entry.amount && entry.currency ? formatCurrency(entry.amount, entry.currency) : 'Amount not available'}
                        </p>
                          </div>
                          <div className="text-right text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(entry.sentAt)}
                            </div>
                            {entry.notes && (
                              <div className="mt-1 text-xs text-gray-400">
                                {entry.notes.substring(0, 50)}...
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedEntry(entry)
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            // Create a minimal invoice object from archive entry data
                            const invoice: Invoice = {
                              client: entry.client || 'Unknown Client',
                              number: entry.invoiceNumber || entry.invoiceId,
                              amount: entry.amount || 0,
                              currency: entry.currency || 'EUR',
                              dueDate: new Date(),
                              email: 'Unknown',
                              description: entry.notes
                            }
                            onRestore(entry.id, invoice)
                          }}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Entry Detail Dialog */}
        {selectedEntry && (
          <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Archive Entry - {selectedEntry.client || 'Unknown Client'}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Invoice Details */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Invoice Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
                    <div><strong>Client:</strong> {selectedEntry.client || 'Unknown Client'}</div>
                    <div><strong>Invoice:</strong> {selectedEntry.invoiceNumber || selectedEntry.invoiceId}</div>
                    <div><strong>Amount:</strong> {selectedEntry.amount && selectedEntry.currency ? formatCurrency(selectedEntry.amount, selectedEntry.currency) : 'Not available'}</div>
                    <div><strong>Archived:</strong> {formatDate(selectedEntry.sentAt)}</div>
                    {selectedEntry.notes && (
                      <div className="col-span-2"><strong>Notes:</strong> {selectedEntry.notes}</div>
                    )}
                  </div>
                </div>

                {/* Email Content */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Email Subject</label>
                    <div className="p-3 bg-gray-50 border rounded-md">
                      {selectedEntry.emailSubject}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Email Body</label>
                    <div className="p-4 bg-gray-50 border rounded-md whitespace-pre-wrap text-sm max-h-48 overflow-y-auto">
                      {selectedEntry.emailBody}
                    </div>
                  </div>
                </div>

                {/* Metadata */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Archive Details</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div><strong>Status:</strong> {selectedEntry.status}</div>
                    <div><strong>Archived:</strong> {formatDate(selectedEntry.sentAt)}</div>
                    <div><strong>Template:</strong> {selectedEntry.emailTemplateName}</div>
                    {selectedEntry.notes && (
                      <div><strong>Notes:</strong> {selectedEntry.notes}</div>
                    )}
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  )
} 